// pages/PracticeNumberQueue.jsx — Admin Board tab
// Phase 9 (July 2026 batch): Practice Number Verification & Uniqueness Workflow
//
// Shows firms whose registered practice number conflicted with another
// firm's already-active practice number at signup. For each conflict:
// the applicant's submitted documents, the existing holder's contact
// details (so the reviewer can confirm with them directly), and
// approve/reject actions.
//
// Widened Aug 2026 (kailux-fixes-log.md §10) to also cover conflicts on
// individual_member_number and reg_number, not just practice_number —
// conflict_field on each entry says which one actually triggered it, so
// the reviewer isn't told "practice number" when it was really a reg
// number or individual member number match.
import { useState, useEffect } from 'react';
import { getPracticeNumberQueue, approvePracticeNumber, rejectPracticeNumber } from '../api/kailux';
import EmptyState from '../components/EmptyState';
import Spinner from '../components/Spinner';
import { FileText, CheckCircle2, XCircle, Mail } from 'lucide-react';
import { T } from '../theme/theme'

const CONFLICT_FIELD_LABELS = {
  practice_number: 'Practice number',
  individual_member_number: 'Individual member number',
  reg_number: 'Registration number',
}


export default function PracticeNumberQueue({ onQueueChanged }) {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [notesById, setNotesById] = useState({});

  const load = () => {
    setLoading(true);
    getPracticeNumberQueue(statusFilter)
      .then(d => setEntries(d.entries || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, [statusFilter]);

  const decide = async (entry, action) => {
    setBusyId(entry.id);
    try {
      const notes = notesById[entry.id] || '';
      if (action === 'approve') await approvePracticeNumber(entry.id, notes);
      else await rejectPracticeNumber(entry.id, notes);
      setEntries(es => es.filter(e => e.id !== entry.id));
      if (onQueueChanged) onQueueChanged();
    } catch (err) {
      alert(err.response?.data?.detail || `Could not ${action} this entry.`);
    } finally {
      setBusyId(null);
    }
  };

  const tabBtn = (key, label) => (
    <button onClick={() => setStatusFilter(key)}
      style={{ padding:'8px 14px', borderRadius:'8px', border:`1px solid ${statusFilter===key?T.or:T.bd}`,
        background: statusFilter===key ? T.ord : 'transparent', color: statusFilter===key ? T.or : T.mt,
        fontSize:'13px', fontWeight:600, cursor:'pointer' }}>
      {label}
    </button>
  );

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px' }}>
        <h1 style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px', fontWeight:800, color:T.tx, margin:0 }}>
          Practice Number Verification
        </h1>
        <div style={{ display:'flex', gap:'8px' }}>
          {tabBtn('pending', 'Pending')}
          {tabBtn('approved', 'Approved')}
          {tabBtn('rejected', 'Rejected')}
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : entries.length === 0 ? (
        <EmptyState title="Nothing here" message={`No ${statusFilter} practice number conflicts.`} />
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
          {entries.map(entry => (
            <div key={entry.id} style={{ background:T.s1, border:`1px solid ${T.bd}`,
              borderRadius:'12px', padding:'20px' }}>

              <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
                <div>
                  <div style={{ fontSize:'12px', color:T.mt, marginBottom:'4px' }}>Applicant (new signup)</div>
                  <div style={{ fontSize:'15px', fontWeight:700, color:T.tx }}>
                    {entry.applicant?.name || 'Unknown firm'}
                  </div>
                  <div style={{ fontSize:'13px', color:T.lt }}>{entry.applicant?.email}</div>
                </div>
                <div>
                  <div style={{ fontSize:'12px', color:T.mt, marginBottom:'4px' }}>
                    {CONFLICT_FIELD_LABELS[entry.conflict_field] || 'Practice number'} in conflict
                  </div>
                  <div style={{ fontSize:'15px', fontWeight:700, color:T.am }}>{entry.practice_number}</div>
                  <div style={{ fontSize:'12px', color:T.mt }}>{entry.regulatory_body || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize:'12px', color:T.mt, marginBottom:'4px' }}>Existing holder</div>
                  <div style={{ fontSize:'15px', fontWeight:700, color:T.tx }}>
                    {entry.existing?.name || 'Unknown firm'}
                  </div>
                  {entry.existing?.email && (
                    <a href={`mailto:${entry.existing.email}?subject=Confirming%20practice%20number%20${encodeURIComponent(entry.practice_number)}`}
                      style={{ fontSize:'13px', color:T.or, display:'flex', alignItems:'center', gap:'4px' }}>
                      <Mail size={12} /> Contact to confirm
                    </a>
                  )}
                </div>
              </div>

              <div style={{ marginTop:'14px' }}>
                <div style={{ fontSize:'12px', color:T.mt, marginBottom:'6px' }}>Submitted documents</div>
                {(entry.documents || []).length === 0 ? (
                  <div style={{ fontSize:'13px', color:T.mt, fontStyle:'italic' }}>No documents submitted yet.</div>
                ) : (
                  <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
                    {entry.documents.map((d, i) => (
                      <a key={i} href={d.signed_url || '#'} target="_blank" rel="noreferrer"
                        style={{ display:'flex', alignItems:'center', gap:'6px', padding:'8px 12px',
                          background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'8px',
                          fontSize:'12px', color:T.tx, textDecoration:'none' }}>
                        <FileText size={14} color={T.mt} /> {d.filename || 'document'}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {statusFilter === 'pending' && (
                <div style={{ marginTop:'16px', display:'flex', gap:'10px', alignItems:'center' }}>
                  <input placeholder="Optional note (visible internally only)"
                    value={notesById[entry.id] || ''}
                    onChange={e => setNotesById(n => ({ ...n, [entry.id]: e.target.value }))}
                    style={{ flex:1, padding:'10px 12px', background:T.s3, border:`1px solid ${T.bd}`,
                      borderRadius:'8px', color:T.tx, fontSize:'13px', outline:'none' }} />
                  <button onClick={() => decide(entry, 'approve')} disabled={busyId === entry.id}
                    style={{ display:'flex', alignItems:'center', gap:'6px', padding:'10px 16px',
                      borderRadius:'8px', border:'none', background:T.gn, color:T.onpri,
                      fontSize:'13px', fontWeight:700, cursor: busyId===entry.id ? 'not-allowed' : 'pointer' }}>
                    <CheckCircle2 size={14} /> Approve
                  </button>
                  <button onClick={() => decide(entry, 'reject')} disabled={busyId === entry.id}
                    style={{ display:'flex', alignItems:'center', gap:'6px', padding:'10px 16px',
                      borderRadius:'8px', border:'none', background:T.rd, color:T.onpri,
                      fontSize:'13px', fontWeight:700, cursor: busyId===entry.id ? 'not-allowed' : 'pointer' }}>
                    <XCircle size={14} /> Reject
                  </button>
                </div>
              )}

              {entry.admin_notes && statusFilter !== 'pending' && (
                <div style={{ marginTop:'12px', fontSize:'12px', color:T.mt, fontStyle:'italic' }}>
                  Note: {entry.admin_notes}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
